import React, { memo, useContext, useState } from 'react'
import PropTypes from 'prop-types'

import Modal from '../modal'
import ModalBody from '../modalBody'
import FormGroup from '../formGroup'
import FormLabel from '../formLabel'
import FormInputGroup from '../formInputGroup'
import FormInputAddon from '../formInputAddon'
import FormInput from '../formInput'
import Button from '../button'
import { translate } from '../../helpers/stringHelpers'
import Alert from '../alert'
import OffersList from '../offersList'
import FeesAlert from '../feesAlert'
import ConfigContext from '../../contexts/configContext'
import trackingEvents from '../../constants/trackingEvents'
import { listings, listingSales, notifications } from '../../actions'
import { trackEvent } from '../../withTracking'
import { toast } from 'react-toastify'
import { formatCurrency } from '../../helpers/currencyHelpers'
import PURCHASE_TYPES from '../../constants/purchaseTypes'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

const {
  CATEGORY_LISTING,
  ACTION_TENDER
} = trackingEvents

const PurchaseConfirmationModalTender = ({
  isOpen,
  listingUuid,
  onClose
}) => {
  const { locale } = useContext(ConfigContext)

  const [isAwaitingResponse, setAwaitingResponse] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const dispatch = useDispatch()
  const listing = useSelector(state => state.lots.listings[listingUuid], shallowEqual)
  const currencyCode = useSelector(state => listingUuid && state.listingSales.sales[listingUuid].currencyCode)
  const offers = useSelector(state => state.offers.offersByListing[listingUuid]
    ?.map(offerUuid => state.offers.offers[offerUuid]), shallowEqual)
  const userUuid = useSelector(state => state.users.user.uuid)
  const userId = useSelector(state => state.users.user.id)
  const auctionUuid = listing?.isMarketplace ? 'marketplace' : listing?.auctionUuid
  const isFavourited = useSelector(state => state.lots.favourites[auctionUuid]?.[listingUuid])
  const registrantId = useSelector(state => (userUuid && state.registrations.registrantIdsByUserUuidByAuction[listing?.auctionUuid]?.[userUuid]?.id))
  const maximumOffersPerItem = useSelector(state => state.auctions.configurations[auctionUuid]?.maximumOffersPerItem)
  const isComplete = useSelector(state => state.saleStatuses.saleStatuses[listingUuid]?.complete)
  const purchaseTypeId = useSelector(state => state.listingSales.sales[listingUuid]?.purchaseTypeId)
  const unitOfMeasurement = useSelector(state => state.listingSales.sales[listingUuid]?.unitOfMeasurement)

  if (purchaseTypeId !== PURCHASE_TYPES.TENDER) return null
  const isAtMaxOffers = (offers && maximumOffersPerItem <= offers.length)
  const onChange = (e) => {
    const value = e.target.value
    const valueEmpty = !value
    const doesTargetMatchesPattern = /^\d+([.]\d{0,2})?$/.test(value)

    if (valueEmpty || doesTargetMatchesPattern) {
      setInputValue(e.target.value)
    }
  }

  const handleTenderConfirm = async (e) => {
    e.preventDefault()
    try {
      setAwaitingResponse(true)
      const offerEvent = await dispatch(listingSales.placeOffer(listingUuid, listing.id, registrantId, userUuid, userId, inputValue, true))
      trackEvent(CATEGORY_LISTING, ACTION_TENDER, listingUuid, inputValue)
      if (offerEvent) {
        if (offerEvent.offer.rejected) {
          toast.error(translate('JspPublicCodes_JSP_LISTING_OFFER_REJECTED')
            .replace('{0}', formatCurrency(locale, currencyCode, inputValue))
            .replace('{1}', listing.lotNumber))
        } else {
          onClose()
          const successMessage = translate('JspPublicCodes_JSP_LISTING_OFFER_PENDING')
            .replace('{0}', formatCurrency(locale, currencyCode, inputValue))
            .replace('{1}', listing.lotNumber)

          if (!isFavourited) {
            dispatch(listings.favouriteToggle(auctionUuid, listingUuid, listing.id, userId))
          }
          setInputValue('')
          dispatch(notifications.addAuctionNotification(auctionUuid, listingUuid, 'info', successMessage))
        }
      }
    } catch (err) {
      console.error(err)
      toast.error(err)
    }
    onClose()
    setAwaitingResponse()
  }

  return (
    <Modal onClose={onClose} isOpen={isOpen} id='tenderModal'>
      <ModalBody>
        <h4 className='modal__primary-title'>{translate('JspPublicCodes_JSP_BIDDING_TENDER')}</h4>
        <h5 className='modal__heading--tender'>{listing.title}</h5>
        <OffersList listingUuid={listingUuid}/>
        <form
          onSubmit={handleTenderConfirm}
        >
          {isAtMaxOffers
            ? (
              <Alert color='warning'>{translate('JspPublicCodes_JSP_LISTING_OFFER_MAX_ATTEMPTS')}</Alert>
              )
            : isComplete
              ? (
                <Alert color='warning'>{translate('JspPublicCodes_JSP_SALE_ENDED')}</Alert>
                )
              : (
          <FormGroup>
            <FormLabel for='tender__input'>{translate('JspPublicCodes_PURCHASE_DIALOG_ID_LIKE_TO_OFFER')}</FormLabel>
            <FormInputGroup>
              <FormInputAddon>{currencyCode}</FormInputAddon>
              <FormInput
                dataRef='confirmationInput'
                onChange={onChange}
                id='tender__input'
                inputMode='number'
                pattern='^\d+([.]\d{0,2})?$'
                type='text'
                value={inputValue}
                step='.01'
              />
              <FormInputAddon>
                {unitOfMeasurement}
              </FormInputAddon>
            </FormInputGroup>
          </FormGroup>
                )}
          <p dangerouslySetInnerHTML={{ __html: translate('PageCodes_PAGE_TENDER_HELP') }}/>
          <FeesAlert
            listingUuid={listingUuid}
          />
          <Button
            id='tender__cancel'
            onClick={onClose}
          >
            {translate('JSTextCodes_CANCEL')}
          </Button>
          <Button
            disabled={isAwaitingResponse || isAtMaxOffers || isComplete}
            id='tender__submit'
            type='submit'
            color='primary'
            className='pull-right'
          >
            {translate('JspPublicCodes_JSP_BIDDING_TENDER')}
          </Button>
        </form>
      </ModalBody>
    </Modal>
  )
}

PurchaseConfirmationModalTender.propTypes = {
  isOpen: PropTypes.bool,
  listingUuid: PropTypes.string,
  onClose: PropTypes.func.isRequired
}

const isEqual = (a, b) => a.isOpen === b.isOpen &&
  a.listingUuid === b.listingUuid

export default memo(PurchaseConfirmationModalTender, isEqual)
